import React from "react";
import WorldCupSticker from "./WorldCupSticker";

const BracketPlayer = ({ professor, rank, isWinner, score, onClick, width = 80, height = 106 }) => {
    if (!professor) {
        return (
            <div 
                className="d-flex align-items-center justify-content-center"
                style={{ 
                    width: `${width}px`, 
                    height: `${height}px`, 
                    background: "rgba(255,255,255,0.03)", 
                    border: "2px dashed rgba(255,255,255,0.15)",
                    borderRadius: "10px",
                    opacity: 0.5 
                }}
            >
                <span className="font-size-9 text-white-50 fw-medium">Aguardando...</span>
            </div>
        );
    }

    return (
        <div 
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
            className="position-relative d-flex flex-column align-items-center"
            style={{ 
                cursor: "pointer",
                transition: "all 0.3s"
            }}
        >
            <WorldCupSticker 
                professor={professor} 
                rank={rank} 
                size="custom" 
                style={{ 
                    width: `${width}px`, 
                    height: `${height}px`,
                    borderRadius: "10px",
                    border: isWinner ? "3px solid #FFE066" : "1px solid rgba(255,255,255,0.15)",
                    boxShadow: isWinner 
                        ? "0 0 15px rgba(255,224,102,0.4)" 
                        : "0 4px 10px rgba(0,0,0,0.3)"
                }} 
            />
            {/* Score Badge */}
            <div 
                className="position-absolute"
                style={{
                    bottom: "-8px",
                    zIndex: 12,
                    background: isWinner ? "#FFE066" : "rgba(5, 20, 8, 0.9)",
                    color: isWinner ? "#051408" : "#fff",
                    border: isWinner ? "none" : "1px solid rgba(255,255,255,0.2)",
                    borderRadius: "10px",
                    padding: "2px 8px",
                    fontSize: "9px",
                    fontWeight: "800",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.4)"
                }}
            >
                {Math.round(score)}%
            </div>
        </div>
    );
};

const BracketMatch = ({ player1, player2, rank1, rank2, score1, score2, title, onSelectPlayer, width = 210, playerWidth = 80, playerHeight = 106 }) => {
    const isP1Winner = score1 > score2;
    const isP2Winner = score2 > score1;

    return (
        <div 
            className="d-flex flex-column align-items-center p-2 position-relative"
            style={{ 
                background: "rgba(5, 20, 8, 0.75)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.12)",
                borderRadius: "18px",
                width: `${width}px`,
                boxShadow: "0 8px 20px rgba(0,0,0,0.4)"
            }}
        >
            {title && (
                <div 
                    className="text-center font-size-9 fw-black text-warning mb-2 text-uppercase"
                    style={{ letterSpacing: "1px", opacity: 0.8 }}
                >
                    {title}
                </div>
            )}
            <div className="d-flex align-items-center gap-1 w-100 justify-content-center">
                <BracketPlayer professor={player1} rank={rank1} isWinner={isP1Winner} score={score1} onClick={() => onSelectPlayer(player1)} width={playerWidth} height={playerHeight} />
                
                <div 
                    className="font-size-9 fw-black text-white-50 px-1"
                    style={{ 
                        letterSpacing: "0.5px", 
                        textShadow: "0 0 5px rgba(255,255,255,0.2)",
                        marginTop: "-8px" // Compensa visualmente a tag de Score
                    }}
                >
                    VS
                </div>
                
                <BracketPlayer professor={player2} rank={rank2} isWinner={isP2Winner} score={score2} onClick={() => onSelectPlayer(player2)} width={playerWidth} height={playerHeight} />
            </div>
        </div>
    );
};

const WorldCupBracket = ({ collaborators, onSelectPlayer }) => {
    // Sizing system - smaller at the base, progressively larger as we go up
    
    // Base row: Quartas de Final (QF)
    const PLAYER_WIDTH_QF = 80;
    const PLAYER_HEIGHT_QF = 106;
    const MATCH_WIDTH_QF = 210;
    const GAP_QF = 12; 

    // Middle row: Semifinais (SF)
    const PLAYER_WIDTH_SF = 95;
    const PLAYER_HEIGHT_SF = 126;
    const MATCH_WIDTH_SF = 240;
    const GAP_SF = 32;

    // Top row: Grande Final (F)
    const PLAYER_WIDTH_F = 110;
    const PLAYER_HEIGHT_F = 146;
    const MATCH_WIDTH_F = 270;

    // Dynamic calculations for Case 1 (8 or more players)
    const totalQFWidth = 4 * MATCH_WIDTH_QF + 3 * GAP_QF;
    const totalSFWidth = 2 * MATCH_WIDTH_SF + GAP_SF;
    const sfOffset = (totalQFWidth - totalSFWidth) / 2;

    const qf1 = MATCH_WIDTH_QF / 2;
    const qf2 = qf1 + MATCH_WIDTH_QF + GAP_QF;
    const qf3 = qf2 + MATCH_WIDTH_QF + GAP_QF;
    const qf4 = qf3 + MATCH_WIDTH_QF + GAP_QF;

    const sf1 = sfOffset + MATCH_WIDTH_SF / 2;
    const sf2 = sfOffset + MATCH_WIDTH_SF + GAP_SF + MATCH_WIDTH_SF / 2;

    // Ordenar colaboradores por score
    const sorted = React.useMemo(() => {
        return [...(collaborators || [])].sort((a, b) => b.scoreFinal - a.scoreFinal);
    }, [collaborators]);
    const count = sorted.length;
    const selectHandler = onSelectPlayer || (() => {});

    // Target sizes for responsive scale
    const targetWidth = count >= 8 ? totalQFWidth : (count >= 4 ? totalSFWidth : MATCH_WIDTH_F);
    const targetHeight = count >= 8 ? 980 : (count >= 4 ? 740 : 440);

    const containerRef = React.useRef(null);
    const [scale, setScale] = React.useState(1);

    React.useEffect(() => {
        if (!containerRef.current || count === 0) return;

        const handleResize = (entries) => {
            for (let entry of entries) {
                const width = entry.contentRect.width;
                if (width < targetWidth) {
                    const newScale = (width - 16) / targetWidth;
                    setScale(Math.max(0.35, newScale)); // Don't scale below 35%
                } else {
                    setScale(1);
                }
            }
        };

        const observer = new ResizeObserver(handleResize);
        const parent = containerRef.current.parentElement;
        if (parent) {
            observer.observe(parent);
        } else {
            observer.observe(containerRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [targetWidth, count]);

    if (!collaborators || collaborators.length === 0) return null;

    let innerContent = null;

    // Caso 1: 8 ou mais jogadores -> Quartas de Final (Pyramid Layout)
    if (count >= 8) {
        const qf1_1 = sorted[0]; const qf1_2 = sorted[7];
        const qf2_1 = sorted[3]; const qf2_2 = sorted[4];
        const qf3_1 = sorted[1]; const qf3_2 = sorted[6];
        const qf4_1 = sorted[2]; const qf4_2 = sorted[5];

        const sf1_1 = qf1_1.scoreFinal > qf1_2.scoreFinal ? qf1_1 : qf1_2;
        const sf1_2 = qf2_1.scoreFinal > qf2_2.scoreFinal ? qf2_1 : qf2_2;
        const sf2_1 = qf3_1.scoreFinal > qf3_2.scoreFinal ? qf3_1 : qf3_2;
        const sf2_2 = qf4_1.scoreFinal > qf4_2.scoreFinal ? qf4_1 : qf4_2;

        const f1 = sf1_1.scoreFinal > sf1_2.scoreFinal ? sf1_1 : sf1_2;
        const f2 = sf2_1.scoreFinal > sf2_2.scoreFinal ? sf2_1 : sf2_2;

        const champion = f1.scoreFinal > f2.scoreFinal ? f1 : f2;

        innerContent = (
            <div className="w-100 d-flex flex-column align-items-center gap-2 py-4" style={{ minWidth: `${totalQFWidth}px` }}>
                
                {/* 1. APEX: Campeão da Copa */}
                <div className="d-flex flex-column align-items-center mb-3">
                    <div style={{ position: "relative", width: "100%", display: "flex", justifyContent: "center" }}>
                        <i className="mdi mdi-crown text-warning" style={{ fontSize: "36px", position: "absolute", top: "-24px", filter: "drop-shadow(0 0 10px rgba(241,180,76,0.8))" }}></i>
                    </div>
                    <WorldCupSticker 
                        professor={champion} 
                        rank={1} 
                        size="large" 
                        style={{ 
                            width: "170px", 
                            height: "226px",
                            border: "4px solid #FFE066",
                            boxShadow: "0 0 30px rgba(255,224,102,0.4)"
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            selectHandler(champion);
                        }}
                    />
                    <div className="text-center mt-3 p-2 rounded" style={{ background: "rgba(255, 224, 102, 0.15)", border: "1px solid #FFE066", minWidth: "160px", cursor: "pointer" }} onClick={(e) => {
                        e.stopPropagation();
                        selectHandler(champion);
                    }}>
                        <span className="fw-black text-warning font-size-13 text-uppercase" style={{ letterSpacing: "1.5px" }}>🏆 CAMPEÃO COPA</span>
                    </div>
                </div>

                {/* SVG Conector Final -> Campeão */}
                <svg width="20" height="30" viewBox="0 0 20 30" fill="none" style={{ opacity: 0.8 }}>
                    <line x1="10" y1="0" x2="10" y2="30" stroke="#FFE066" strokeWidth="2.5" strokeDasharray="3 3" />
                </svg>

                {/* 2. GRANDE FINAL */}
                <div className="d-flex justify-content-center">
                    <BracketMatch player1={f1} player2={f2} rank1={1} rank2={2} score1={f1.scoreFinal} score2={f2.scoreFinal} title="Grande Final" onSelectPlayer={selectHandler} width={MATCH_WIDTH_F} playerWidth={PLAYER_WIDTH_F} playerHeight={PLAYER_HEIGHT_F} />
                </div>

                {/* SVG Conectores SF -> Final */}
                <svg width={totalSFWidth} height="40" viewBox={`0 0 ${totalSFWidth} 40`} fill="none" style={{ opacity: 0.7, width: `${totalSFWidth}px` }}>
                    <path d={`M ${MATCH_WIDTH_SF / 2},40 L ${MATCH_WIDTH_SF / 2},20 L ${totalSFWidth - MATCH_WIDTH_SF / 2},20 L ${totalSFWidth - MATCH_WIDTH_SF / 2},40`} stroke="#FFE066" strokeWidth="2" strokeLinecap="round" />
                    <line x1={totalSFWidth / 2} y1="0" x2={totalSFWidth / 2} y2="20" stroke="#FFE066" strokeWidth="2" />
                </svg>

                {/* 3. SEMIFINAIS */}
                <div className="d-flex justify-content-center w-100" style={{ gap: `${GAP_SF}px`, maxWidth: `${totalSFWidth}px` }}>
                    <div style={{ width: `${MATCH_WIDTH_SF}px` }} className="d-flex justify-content-center">
                        <BracketMatch player1={sf1_1} player2={sf1_2} rank1={1} rank2={4} score1={sf1_1.scoreFinal} score2={sf1_2.scoreFinal} title="Semifinal 1" onSelectPlayer={selectHandler} width={MATCH_WIDTH_SF} playerWidth={PLAYER_WIDTH_SF} playerHeight={PLAYER_HEIGHT_SF} />
                    </div>
                    <div style={{ width: `${MATCH_WIDTH_SF}px` }} className="d-flex justify-content-center">
                        <BracketMatch player1={sf2_1} player2={sf2_2} rank1={2} rank2={3} score1={sf2_1.scoreFinal} score2={sf2_2.scoreFinal} title="Semifinal 2" onSelectPlayer={selectHandler} width={MATCH_WIDTH_SF} playerWidth={PLAYER_WIDTH_SF} playerHeight={PLAYER_HEIGHT_SF} />
                    </div>
                </div>

                {/* SVG Conectores QF -> SF */}
                <svg width={totalQFWidth} height="40" viewBox={`0 0 ${totalQFWidth} 40`} fill="none" style={{ opacity: 0.6, width: `${totalQFWidth}px` }}>
                    {/* Par Esquerdo: Q1 e Q2 para SF1 */}
                    <path d={`M ${qf1},40 L ${qf1},20 L ${qf2},20 L ${qf2},40`} stroke="#FFE066" strokeWidth="2" strokeLinecap="round" />
                    <line x1={sf1} y1="0" x2={sf1} y2="20" stroke="#FFE066" strokeWidth="2" />
                    
                    {/* Par Direito: Q3 e Q4 para SF2 */}
                    <path d={`M ${qf3},40 L ${qf3},20 L ${qf4},20 L ${qf4},40`} stroke="#FFE066" strokeWidth="2" strokeLinecap="round" />
                    <line x1={sf2} y1="0" x2={sf2} y2="20" stroke="#FFE066" strokeWidth="2" />
                </svg>

                {/* 4. QUARTAS DE FINAL (Base) */}
                <div className="d-flex justify-content-center w-100" style={{ gap: `${GAP_QF}px`, maxWidth: `${totalQFWidth}px` }}>
                    <BracketMatch player1={qf1_1} player2={qf1_2} rank1={1} rank2={8} score1={qf1_1.scoreFinal} score2={qf1_2.scoreFinal} title="Quartas 1" onSelectPlayer={selectHandler} width={MATCH_WIDTH_QF} playerWidth={PLAYER_WIDTH_QF} playerHeight={PLAYER_HEIGHT_QF} />
                    <BracketMatch player1={qf2_1} player2={qf2_2} rank1={4} rank2={5} score1={qf2_1.scoreFinal} score2={qf2_2.scoreFinal} title="Quartas 2" onSelectPlayer={selectHandler} width={MATCH_WIDTH_QF} playerWidth={PLAYER_WIDTH_QF} playerHeight={PLAYER_HEIGHT_QF} />
                    <BracketMatch player1={qf3_1} player2={qf3_2} rank1={2} rank2={7} score1={qf3_1.scoreFinal} score2={qf3_2.scoreFinal} title="Quartas 3" onSelectPlayer={selectHandler} width={MATCH_WIDTH_QF} playerWidth={PLAYER_WIDTH_QF} playerHeight={PLAYER_HEIGHT_QF} />
                    <BracketMatch player1={qf4_1} player2={qf4_2} rank1={3} rank2={6} score1={qf4_1.scoreFinal} score2={qf4_2.scoreFinal} title="Quartas 4" onSelectPlayer={selectHandler} width={MATCH_WIDTH_QF} playerWidth={PLAYER_WIDTH_QF} playerHeight={PLAYER_HEIGHT_QF} />
                </div>

            </div>
        );
    }

    // Caso 2: 4 a 7 jogadores -> Semifinais e Finais (Top 4 Pyramid)
    if (count >= 4 && count < 8) {
        const sf1_1 = sorted[0]; const sf1_2 = sorted[3];
        const sf2_1 = sorted[1]; const sf2_2 = sorted[2];

        const f1 = sf1_1.scoreFinal > sf1_2.scoreFinal ? sf1_1 : sf1_2;
        const f2 = sf2_1.scoreFinal > sf2_2.scoreFinal ? sf2_1 : sf2_2;

        const champion = f1.scoreFinal > f2.scoreFinal ? f1 : f2;

        innerContent = (
            <div className="w-100 d-flex flex-column align-items-center gap-2 py-4" style={{ minWidth: `${totalSFWidth}px` }}>
                
                {/* 1. APEX: Campeão da Copa */}
                <div className="d-flex flex-column align-items-center mb-3">
                    <div style={{ position: "relative", width: "100%", display: "flex", justifyContent: "center" }}>
                        <i className="mdi mdi-crown text-warning" style={{ fontSize: "36px", position: "absolute", top: "-24px", filter: "drop-shadow(0 0 10px rgba(241,180,76,0.8))" }}></i>
                    </div>
                    <WorldCupSticker 
                        professor={champion} 
                        rank={1} 
                        size="large" 
                        style={{ 
                            width: "170px", 
                            height: "226px",
                            border: "4px solid #FFE066",
                            boxShadow: "0 0 30px rgba(255,224,102,0.4)"
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            selectHandler(champion);
                        }}
                    />
                    <div className="text-center mt-3 p-2 rounded" style={{ background: "rgba(255, 224, 102, 0.15)", border: "1px solid #FFE066", minWidth: "160px", cursor: "pointer" }} onClick={(e) => {
                        e.stopPropagation();
                        selectHandler(champion);
                    }}>
                        <span className="fw-black text-warning font-size-13 text-uppercase" style={{ letterSpacing: "1.5px" }}>🏆 CAMPEÃO COPA</span>
                    </div>
                </div>

                {/* SVG Conector Final -> Campeão */}
                <svg width="20" height="30" viewBox="0 0 20 30" fill="none" style={{ opacity: 0.8 }}>
                    <line x1="10" y1="0" x2="10" y2="30" stroke="#FFE066" strokeWidth="2.5" strokeDasharray="3 3" />
                </svg>

                {/* 2. GRANDE FINAL */}
                <div className="d-flex justify-content-center">
                    <BracketMatch player1={f1} player2={f2} rank1={1} rank2={2} score1={f1.scoreFinal} score2={f2.scoreFinal} title="Grande Final" onSelectPlayer={selectHandler} width={MATCH_WIDTH_F} playerWidth={PLAYER_WIDTH_F} playerHeight={PLAYER_HEIGHT_F} />
                </div>

                {/* SVG Conectores SF -> Final */}
                <svg width={totalSFWidth} height="40" viewBox={`0 0 ${totalSFWidth} 40`} fill="none" style={{ opacity: 0.7, width: `${totalSFWidth}px` }}>
                    <path d={`M ${MATCH_WIDTH_SF / 2},40 L ${MATCH_WIDTH_SF / 2},20 L ${totalSFWidth - MATCH_WIDTH_SF / 2},20 L ${totalSFWidth - MATCH_WIDTH_SF / 2},40`} stroke="#FFE066" strokeWidth="2" strokeLinecap="round" />
                    <line x1={totalSFWidth / 2} y1="0" x2={totalSFWidth / 2} y2="20" stroke="#FFE066" strokeWidth="2" />
                </svg>

                {/* 3. SEMIFINAIS (Base) */}
                <div className="d-flex justify-content-center w-100" style={{ gap: `${GAP_SF}px`, maxWidth: `${totalSFWidth}px` }}>
                    <div style={{ width: `${MATCH_WIDTH_SF}px` }} className="d-flex justify-content-center">
                        <BracketMatch player1={sf1_1} player2={sf1_2} rank1={1} rank2={4} score1={sf1_1.scoreFinal} score2={sf1_2.scoreFinal} title="Semifinal 1" onSelectPlayer={selectHandler} width={MATCH_WIDTH_SF} playerWidth={PLAYER_WIDTH_SF} playerHeight={PLAYER_HEIGHT_SF} />
                    </div>
                    <div style={{ width: `${MATCH_WIDTH_SF}px` }} className="d-flex justify-content-center">
                        <BracketMatch player1={sf2_1} player2={sf2_2} rank1={2} rank2={3} score1={sf2_1.scoreFinal} score2={sf2_2.scoreFinal} title="Semifinal 2" onSelectPlayer={selectHandler} width={MATCH_WIDTH_SF} playerWidth={PLAYER_WIDTH_SF} playerHeight={PLAYER_HEIGHT_SF} />
                    </div>
                </div>

            </div>
        );
    }

    // Caso 3: Menos de 4 jogadores -> Grande Final direta (Top 2)
    if (count < 4) {
        const f1 = sorted[0];
        const f2 = sorted[1];
        const champion = f1.scoreFinal > (f2?.scoreFinal || 0) ? f1 : f2;

        innerContent = (
            <div className="w-100 d-flex flex-column align-items-center gap-2 py-4">
                
                {/* 1. APEX: Campeão */}
                <div className="d-flex flex-column align-items-center mb-3">
                    <div style={{ position: "relative", width: "100%", display: "flex", justifyContent: "center" }}>
                        <i className="mdi mdi-crown text-warning" style={{ fontSize: "36px", position: "absolute", top: "-24px", filter: "drop-shadow(0 0 10px rgba(241,180,76,0.8))" }}></i>
                    </div>
                    <WorldCupSticker 
                        professor={champion} 
                        rank={1} 
                        size="large" 
                        style={{ 
                            width: "170px", 
                            height: "226px",
                            border: "4px solid #FFE066",
                            boxShadow: "0 0 30px rgba(255,224,102,0.4)"
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            selectHandler(champion);
                        }}
                    />
                    <div className="text-center mt-3 p-2 rounded" style={{ background: "rgba(255, 224, 102, 0.15)", border: "1px solid #FFE066", minWidth: "160px", cursor: "pointer" }} onClick={(e) => {
                        e.stopPropagation();
                        selectHandler(champion);
                    }}>
                        <span className="fw-black text-warning font-size-13 text-uppercase" style={{ letterSpacing: "1.5px" }}>🏆 CAMPEÃO COPA</span>
                    </div>
                </div>

                {/* SVG Conector Final -> Campeão */}
                <svg width="20" height="30" viewBox="0 0 20 30" fill="none" style={{ opacity: 0.8 }}>
                    <line x1="10" y1="0" x2="10" y2="30" stroke="#FFE066" strokeWidth="2.5" strokeDasharray="3 3" />
                </svg>

                {/* 2. GRANDE FINAL (Base) */}
                <div className="d-flex justify-content-center">
                    <BracketMatch player1={f1} player2={f2} rank1={1} rank2={2} score1={f1.scoreFinal} score2={f2?.scoreFinal || 0} title="Grande Final" onSelectPlayer={selectHandler} width={MATCH_WIDTH_F} playerWidth={PLAYER_WIDTH_F} playerHeight={PLAYER_HEIGHT_F} />
                </div>

            </div>
        );
    }

    return (
        <div 
            ref={containerRef}
            style={{ 
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                overflow: "visible"
            }}
        >
            <div style={{
                transform: `scale(${scale})`,
                transformOrigin: "top center",
                width: `${targetWidth}px`,
                marginBottom: `${targetHeight * (scale - 1)}px`,
                transition: "transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), margin-bottom 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)"
            }}>
                {innerContent}
            </div>
        </div>
    );
};

export default WorldCupBracket;
